import { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';

import { Registry } from '../../FormBuilderLibrary';
import { stringToHTML } from '../utils';
import StyledWrapper from './style';

import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import FormHelperText from '@mui/material/FormHelperText';
import { getElementIcon } from '../utils';
import Tooltip from '@mui/material/Tooltip';
import FormControl from '@mui/material/FormControl';

const MUICheckbox = forwardRef((props, ref) => {
	const { defaultValue, disabled, isFormBuilder, name } = props;

	const [hasError, setHasError] = useState(props.data?.error || false);
	const [checkedOptions, setCheckedOptions] = useState(() => {
		if (Array.isArray(defaultValue)) {
			return defaultValue;
		}
		return [];
	});

	useImperativeHandle(
		ref,
		() => ({
			get value() {
				return checkedOptions;
			},
			getValue: () => checkedOptions,
			getCheckedOptions: () => checkedOptions
		}),
		[checkedOptions]
	);

	const parsedLabel = useMemo(() => stringToHTML(props?.data?.label), [props?.data?.label]);
	const parsedHelperText = useMemo(
		() => stringToHTML(props?.data?.help_message),
		[props?.data?.help_message]
	);
	const [isOverflowing, setIsOverflowing] = useState(false);
	const labelRef = useRef(null);

	const handleChange = (optionKey, isChecked) => {
		setHasError(false);
		setCheckedOptions((prev) => {
			if (isChecked) {
				// Add option if not already present
				return prev.includes(optionKey) ? prev : [...prev, optionKey];
			} else {
				// Remove option
				return prev.filter((key) => key !== optionKey);
			}
		});
	};

	useEffect(() => {
		setHasError(props?.data?.error || false);
	}, [props?.data?.error]);

	useEffect(() => {
		// ARM-397. The form elements initially take up the entire width of the screen and then are resized to fit into the proper row size. This setTimeout adds a delay so we do this calculation after the form elements are set to their proper row width. Without this the scroll width and clientWidth will be the same on screens with a width of 2000px and above
		setTimeout(() => {
			if (labelRef.current) {
				setIsOverflowing(labelRef.current.scrollWidth > labelRef.current.clientWidth);
			}
		}, 100);
	});

	const renderCheckboxGroup = (withMarginTop = false) => {
		const OutlineComponent = withMarginTop
			? StyledWrapper.BorderOutlineMarginTop
			: StyledWrapper.BorderOutline;

		return (
			<OutlineComponent
				error={hasError}
				className={'customInputOutline'}
				disabled={disabled ? true : false}
			>
				<FormGroup style={{ width: '100%', minWidth: 0 }}>
					<div style={{ overflowX: props?.data?.inline ? 'auto' : 'unset', width: '100%' }}>
						<div
							style={{
								display: 'flex',
								flexDirection: `${props?.data?.inline ? 'row' : 'column'}`,
								flexWrap: props?.data?.inline ? 'nowrap' : 'wrap'
							}}
						>
							{props?.data?.options.map((option) => {
								const this_key = `preview_${option.key}`;
								const isChecked = checkedOptions.includes(option.key);

								return (
									<div
										key={`Checkbox-${option.key}`}
										style={props?.data?.inline ? { whiteSpace: 'nowrap' } : {}}
									>
										<FormControlLabel
											error={hasError}
											className="disableWhiteBackground noMarginTop"
											disabled={props?.disabled}
											control={
												<Checkbox
													id={`fid_${this_key}`}
													name={`option_${option.key}`}
													checked={isChecked}
													onChange={(e) => handleChange(option.key, e.target.checked)}
													disabled={props?.disabled}
													inputProps={{
														'data-value': option?.value
													}}
												/>
											}
											label={option?.text}
										/>
									</div>
								);
							})}
						</div>
					</div>
				</FormGroup>
			</OutlineComponent>
		);
	};

	return (
		<>
			<FormControl fullWidth name={name}>
				<Tooltip
					arrow
					placement="top"
					title={isOverflowing ? `${props?.data?.label}` : ''}
					enterTouchDelay={0}
				>
					<StyledWrapper.CustomLabel
						value={checkedOptions?.length > 0}
						disabled={disabled ? true : false}
						required={props?.data?.required}
						htmlFor="outlined-adornment-password"
						shrink={true}
					>
						<StyledWrapper.LabelContents ref={labelRef}>
							{isFormBuilder && getElementIcon(props.data?.key)}
							{parsedLabel?.[0] ? parsedLabel : props?.data?.label}
						</StyledWrapper.LabelContents>
						{isFormBuilder && (
							<span>
								{' '}
								{props.data?.unique_identifier
									? `(ff-${props.data?.unique_identifier})`
									: `(tmp-${props.data?.temp_order})`}
							</span>
						)}
					</StyledWrapper.CustomLabel>
				</Tooltip>
				{isFormBuilder ? renderCheckboxGroup(false) : renderCheckboxGroup(true)}
			</FormControl>
			<StyledWrapper.FormHelperTextContainer>
				<FormHelperText error={hasError}>
					{' '}
					{props.data.error?.error ? `${props.data.label} ${props.data.error?.error}` : ''}
				</FormHelperText>
				{parsedHelperText && <FormHelperText>{parsedHelperText}</FormHelperText>}
			</StyledWrapper.FormHelperTextContainer>
		</>
	);
});

MUICheckbox.displayName = 'MUI_Checkbox';
Registry.register('MUI_Checkbox', MUICheckbox);

export default MUICheckbox;
